// <s-drift> — an endlessly drifting photo strip. The [data-drift-track] scroller holds the
// same set of [data-drift-item] tiles repeated three times; the strip keeps scrolling on its own,
// wraps seamlessly between the copies, and scales/fades every tile by its distance from the centre
// so the middle photo reads largest. Buttons with commandfor=<id> and command="--prev"/"--next"
// glide one photo back or forth. The drift runs for every visitor (half speed with
// reduced motion) and pauses only while a finger is on the strip or keyboard focus is inside it.
class SDrift extends HTMLElement {
  #ac = null;
  #track = null;
  #items = [];
  #pos = 0;
  #period = 0;
  #frame = 0;
  #last = 0;
  #paused = false;
  #tween = null;
  #reduced = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#track = this.querySelector("[data-drift-track]");
    if (!this.#track) return;
    this.#items = [...this.#track.querySelectorAll("[data-drift-item]")];
    this.#reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

    const pause = () => { this.#paused = true; };
    const resume = () => { this.#paused = false; this.#last = 0; };
    this.#track.addEventListener("pointerdown", pause, { signal });
    this.#track.addEventListener("pointerup", resume, { signal });
    this.#track.addEventListener("pointercancel", resume, { signal });
    // Keyboard focus pauses the drift so tiles and arrows stay reachable; mouse hover and clicks never pause it.
    this.addEventListener("focusin", (event) => { if (event.target.matches(":focus-visible")) pause(); }, { signal });
    this.addEventListener("focusout", resume, { signal });
    this.#track.addEventListener("scroll", () => this.#syncFromScroll(), { signal, passive: true });
    this.addEventListener("command", (event) => this.#handleCommand(event), { signal });
    window.addEventListener("resize", () => this.#measure(), { signal });

    this.#measure();
    this.#frame = requestAnimationFrame((time) => this.#tick(time));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #measure() {
    const copies = Number(this.dataset.copies) || 3;
    const perCopy = Math.floor(this.#items.length / copies);
    if (perCopy < 1) return;
    this.#period = this.#items[perCopy].offsetLeft - this.#items[0].offsetLeft;
    this.#pos = this.#period;
    this.#track.scrollLeft = this.#pos;
    this.#render();
  }

  #syncFromScroll() {
    if (Math.abs(this.#track.scrollLeft - this.#pos) > 2 && !this.#tween) {
      this.#pos = this.#track.scrollLeft;
    }
  }

  #wrap() {
    if (this.#period <= 0) return;
    if (this.#pos >= this.#period * 2) this.#pos -= this.#period;
    if (this.#pos < this.#period * 0.5) this.#pos += this.#period;
  }

  #handleCommand(event) {
    if (event.command !== "--next" && event.command !== "--prev") return;
    const direction = event.command === "--next" ? 1 : -1;
    const center = this.#pos + this.#track.clientWidth / 2;
    const centers = this.#items.map((item) => item.offsetLeft + item.offsetWidth / 2);
    const candidates = direction > 0
      ? centers.filter((c) => c > center + 4)
      : centers.filter((c) => c < center - 4);
    if (candidates.length === 0) return;
    const target = direction > 0 ? Math.min(...candidates) : Math.max(...candidates);
    this.#tween = { from: this.#pos, to: target - this.#track.clientWidth / 2, start: 0, duration: 550 };
  }

  #tick(time) {
    const delta = this.#last ? Math.min(time - this.#last, 64) : 0;
    this.#last = time;

    if (this.#tween) {
      if (!this.#tween.start) this.#tween.start = time;
      const progress = Math.min((time - this.#tween.start) / this.#tween.duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.#pos = this.#tween.from + (this.#tween.to - this.#tween.from) * eased;
      if (progress >= 1) this.#tween = null;
    } else if (!this.#paused) {
      // Visitors who prefer reduced motion still see the drift, at half speed.
      const speed = (Number(this.dataset.speed) || 40) * (this.#reduced.matches ? 0.5 : 1);
      this.#pos += (speed * delta) / 1000;
    }

    if (!this.#tween) this.#wrap();
    this.#track.scrollLeft = this.#pos;
    this.#render();
    this.#frame = requestAnimationFrame((next) => this.#tick(next));
  }

  #render() {
    const width = this.#track.clientWidth;
    const center = this.#track.scrollLeft + width / 2;
    const reach = Math.max(width / 4, 200);
    const grow = Number(this.dataset.grow) || 0.3;
    for (const item of this.#items) {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const closeness = 1 - Math.min(Math.abs(itemCenter - center) / reach, 1);
      item.style.transform = `scale(${1 + grow * closeness})`;
      item.style.opacity = String(0.5 + 0.5 * closeness);
      item.style.zIndex = closeness > 0.5 ? "2" : "1";
    }
  }
}

if (!customElements.get("s-drift")) {
  customElements.define("s-drift", SDrift);
}
